import type { CampaignFileResponseDto } from '@shared/services/apiService/apiService';

import type { CampaignKnowledgeBaseFile, KnowledgeBaseFileStatus } from './types';

const PROCESSING_STATUSES = new Set(['parsing', 'vectorizing', 'processing']);

const getStatusValue = (status: unknown): string => {
  if (typeof status === 'string') return status.toLowerCase();

  if (status && typeof status === 'object') {
    const value = status as Record<string, unknown>;
    const nestedStatus = value.status ?? value.state ?? value.value;
    return typeof nestedStatus === 'string' ? nestedStatus.toLowerCase() : '';
  }

  return '';
};

export const mapKnowledgeBaseFileStatus = (status: unknown): KnowledgeBaseFileStatus => {
  const normalizedStatus = getStatusValue(status);

  if (normalizedStatus === 'ready') return 'ready';
  if (normalizedStatus === 'failed') return 'failed';
  if (PROCESSING_STATUSES.has(normalizedStatus)) return 'processing';

  return 'processing';
};

export const mapFileResponseToKnowledgeBaseFile = (
  file: CampaignFileResponseDto,
): CampaignKnowledgeBaseFile => ({
  id: file.id,
  fileName: file.fileName,
  size: file.size,
  createdAt: file.createdAt,
  updatedAt: file.updatedAt,
  status: mapKnowledgeBaseFileStatus(file.status),
  errorMessage: file.error ? 'processing-failed' : null,
  indexedChunksCount: file.indexedChunksCount,
  pageCount: file.pageCount,
  isEnabled: file.isEnabled ?? false,
  agentScope: file.agentScope,
  agentIds: file.agentIds,
  hasOriginal: file.hasOriginal,
});

export const isKnowledgeBaseFileProcessing = (file: CampaignKnowledgeBaseFile): boolean =>
  file.status === 'processing';

export const isKnowledgeBaseFileReady = (file: CampaignKnowledgeBaseFile): boolean =>
  file.status === 'ready';

export const isKnowledgeBaseFileFailed = (file: CampaignKnowledgeBaseFile): boolean =>
  file.status === 'failed';

export const isKnowledgeBaseFileAvailable = (file: CampaignKnowledgeBaseFile): boolean =>
  isKnowledgeBaseFileReady(file) && file.isEnabled;

export const hasProcessingKnowledgeBaseFiles = (files: CampaignKnowledgeBaseFile[]): boolean =>
  files.some(isKnowledgeBaseFileProcessing);
